import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

const BACKEND_URL = import.meta.env.VITE_APP_API_BACKEND_URL;

/** `Number(...)` an env var, falling back to `fallback` when unset/blank/NaN. */
function envNumber(value, fallback) {
  const n = Number(value);
  return value != null && value !== '' && Number.isFinite(n) ? n : fallback;
}

// Tunable via VITE_AUDIO_IFRAME_* env vars (see .env.example) — rebuild required to
// pick up a change, same as any other Vite env var.
// This HEAD request crosses the reviewer's own connection (unlike the backend's
// server->GCS connect timeout), so it needs slack for high-latency mobile networks —
// sized for 3G, not just 4G.
const STATUS_CHECK_TIMEOUT_MS = envNumber(import.meta.env.VITE_AUDIO_IFRAME_STATUS_CHECK_TIMEOUT_MS, 15_000);
// Guards the iframe's actual `onLoad` (i.e. the full file finishing). Was 12s, which
// false-positived as "malformed" on slow networks for real, healthy files that just
// took longer than 12s to fully load. Sized for 3G bandwidth on a large file, staying
// safely under the backend's PROXY_MEDIA_STREAM_MAX_DURATION_MS ceiling (10min) so the
// client doesn't give up while the server would still legitimately be streaming.
const ONLOAD_TIMEOUT_MS = envNumber(import.meta.env.VITE_AUDIO_IFRAME_ONLOAD_TIMEOUT_MS, 240_000);
// A validation-rejected check can resolve in milliseconds — keep the spinner up for at
// least this long so the error state doesn't flash in instantly and read as broken/jarring.
const MIN_LOADING_MS = envNumber(import.meta.env.VITE_AUDIO_IFRAME_MIN_LOADING_MS, 4_000);

/**
 * Embeds `audioFileURL` in an iframe with a loading overlay (matches spectrogram / audio preview UX).
 *
 * Before rendering the iframe, does a HEAD status check for `audioFileURL` through the backend's
 * `/proxy-media` route with a timeout. The iframe itself is also routed through `/proxy-media`
 * rather than pointed directly at GCS — our proxy strips ETag/Accept-Ranges from the upstream
 * response, so the browser has no basis to do a conditional Range-based "resume" against a stale
 * cache entry the way it can against GCS directly (confirmed live in prod: a fully-reachable file
 * stuck the iframe forever because Chrome sent a 1-byte `Range`/`If-Range` probe against a
 * partially-cached fragment from an earlier failed load, instead of a normal full GET).
 * Even once the HEAD check passes, the iframe's own `onLoad` isn't trusted unconditionally either
 * — a second, longer timeout falls back to the same error UI if `onLoad` never fires. Without
 * these guards, a hung/unreachable/stuck-loading URL left the spinner stuck forever with no error
 * and no way to recover short of reloading the whole page.
 */
export default function AudioIframe({
  audioFileURL,
  title = 'Audio Preview',
  height = 400,
}) {
  const [loading, setLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);
  const minDisplayTimeoutRef = useRef(null);
  const onLoadTimeoutRef = useRef(null);

  useEffect(() => {
    if (!audioFileURL) return undefined;

    setLoading(true);
    setStatusError(null);

    let cancelled = false;
    const startedAt = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      STATUS_CHECK_TIMEOUT_MS,
    );

    const showError = (message) => {
      const remaining = MIN_LOADING_MS - (Date.now() - startedAt);
      const apply = () => {
        setStatusError(message);
        setLoading(false);
      };
      if (remaining > 0) {
        minDisplayTimeoutRef.current = setTimeout(() => {
          if (!cancelled) apply();
        }, remaining);
      } else {
        apply();
      }
    };

    fetch(
      `${BACKEND_URL}/proxy-media?url=${encodeURIComponent(audioFileURL)}`,
      { method: 'HEAD', signal: controller.signal },
    )
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.error(
            `[AudioIframe] status check failed: ${res.status} for ${audioFileURL}`,
          );
          showError(`Preview unavailable (status ${res.status}).`);
          return;
        }
        // Reachable, but the iframe's own `onLoad` can still fail to fire (seen in
        // practice: a fully-delivered response with no `load` event ever dispatched).
        // Fall back to the same error UI rather than trusting onLoad unconditionally.
        onLoadTimeoutRef.current = setTimeout(() => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.error(
            `[AudioIframe] onLoad never fired within ${ONLOAD_TIMEOUT_MS}ms for ${audioFileURL}`,
          );
          showError('Preview did not finish loading. The file may be malformed.');
        }, ONLOAD_TIMEOUT_MS);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(
          `[AudioIframe] status check ${err?.name === 'AbortError' ? 'timed out' : 'errored'} for ${audioFileURL}`,
          err,
        );
        showError(
          err?.name === 'AbortError'
            ? 'Preview timed out. The file may be unreachable.'
            : 'Failed to load audio preview.',
        );
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      clearTimeout(minDisplayTimeoutRef.current);
      clearTimeout(onLoadTimeoutRef.current);
      controller.abort();
    };
  }, [audioFileURL, retryToken]);

  if (!audioFileURL) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        minHeight: typeof height === 'number' ? `${height}px` : height,
      }}
    >
      {loading && !statusError ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.7)',
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {statusError ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 2,
            textAlign: 'center',
            bgcolor: 'rgba(197,197,212,0.08)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'error.main' }}>
            {statusError}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setRetryToken((t) => t + 1)}
            >
              Retry
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => globalThis.location.reload()}
            >
              Reload Page
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          key={`${audioFileURL}-${retryToken}`}
          component="iframe"
          src={`${BACKEND_URL}/proxy-media?url=${encodeURIComponent(audioFileURL)}`}
          title={title}
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={() => {
            clearTimeout(onLoadTimeoutRef.current);
            setLoading(false);
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            bgcolor: 'transparent',
            visibility: loading ? 'hidden' : 'visible',
          }}
        />
      )}
    </Box>
  );
}
